'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { getLiveWorldCupFixtures, getUpcomingFixtures, getStatusLabel, isInPlay, getFlag, type LiveFixture } from '@/lib/api-football';
import { subscribeToScoreUpdates, subscribeToGoalAnimations, broadcastScoreUpdate, type ScoreUpdatePayload, type GoalAnimationPayload,  } from '@/lib/supabase-realtime';
import Link from 'next/link';
import { RefreshCw, Zap, MessageCircle } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

// ─── Switch date: June 11, 2026 12:00 PM Pacific (PDT = UTC-7) = 19:00 UTC ──
const LIVE_DATA_START_UTC = new Date('2026-06-11T19:00:00.000Z');

function useShouldUseLiveData() {
  const [useLive, setUseLive] = useState(() => new Date() >= LIVE_DATA_START_UTC);
  useEffect(() => {
    const now = new Date();
    if (now >= LIVE_DATA_START_UTC) return;
    const msUntilSwitch = LIVE_DATA_START_UTC.getTime() - now.getTime();
    const timer = setTimeout(() => setUseLive(true), msUntilSwitch);
    return () => clearTimeout(timer);
  }, []);
  return useLive;
}

// ─── Sample live match data ───────────────────────────────────────────────────
const SAMPLE_MATCHES: LiveFixture[] = [
  {
    fixture: {
      id: 9001,
      date: new Date().toISOString(),
      status: { short: '2H', long: 'Second Half', elapsed: 67 },
      venue: { name: 'Estadio Azteca', city: 'Mexico City' },
    },
    league: { id: 1, name: 'FIFA World Cup 2026', country: 'World', logo: '', flag: '', season: 2026, round: 'Group Stage - Matchday 1' },
    teams: {
      home: { id: 1, name: 'Mexico', logo: '', winner: null },
      away: { id: 2, name: 'Poland', logo: 'https://media.api-sports.io/football/teams/24.png', winner: null },
    },
    goals: { home: 2, away: 1 },
    score: { halftime: { home: 1, away: 1 }, fulltime: { home: null, away: null }, extratime: { home: null, away: null }, penalty: { home: null, away: null } },
  },
  {
    fixture: {
      id: 9002,
      date: new Date().toISOString(),
      status: { short: '1H', long: 'First Half', elapsed: 34 },
      venue: { name: 'SoFi Stadium', city: 'Los Angeles' },
    },
    league: { id: 1, name: 'FIFA World Cup 2026', country: 'World', logo: '', flag: '', season: 2026, round: 'Group Stage - Matchday 1' },
    teams: {
      home: { id: 3, name: 'USA', logo: 'https://media.api-sports.io/football/teams/2.png', winner: null },
      away: { id: 4, name: 'England', logo: 'https://media.api-sports.io/football/teams/10.png', winner: null },
    },
    goals: { home: 1, away: 1 },
    score: { halftime: { home: null, away: null }, fulltime: { home: null, away: null }, extratime: { home: null, away: null }, penalty: { home: null, away: null } },
  },
  {
    fixture: {
      id: 9003,
      date: new Date().toISOString(),
      status: { short: 'HT', long: 'Halftime', elapsed: 45 },
      venue: { name: 'MetLife Stadium', city: 'New York' },
    },
    league: { id: 1, name: 'FIFA World Cup 2026', country: 'World', logo: '', flag: '', season: 2026, round: 'Group Stage - Matchday 1' },
    teams: {
      home: { id: 5, name: 'Brazil', logo: 'https://media.api-sports.io/football/teams/6.png', winner: null },
      away: { id: 6, name: 'Argentina', logo: 'https://media.api-sports.io/football/teams/26.png', winner: null },
    },
    goals: { home: 0, away: 1 },
    score: { halftime: { home: 0, away: 1 }, fulltime: { home: null, away: null }, extratime: { home: null, away: null }, penalty: { home: null, away: null } },
  },
  {
    fixture: {
      id: 9004,
      date: (() => { const d = new Date(); d.setHours(d.getHours() + 2); return d.toISOString(); })(),
      status: { short: 'NS', long: 'Not Started', elapsed: null },
      venue: { name: 'AT&T Stadium', city: 'Dallas' },
    },
    league: { id: 1, name: 'FIFA World Cup 2026', country: 'World', logo: '', flag: '', season: 2026, round: 'Group Stage - Matchday 1' },
    teams: {
      home: { id: 7, name: 'France', logo: 'https://media.api-sports.io/football/teams/2.png', winner: null },
      away: { id: 8, name: 'Germany', logo: 'https://media.api-sports.io/football/teams/25.png', winner: null },
    },
    goals: { home: null, away: null },
    score: { halftime: { home: null, away: null }, fulltime: { home: null, away: null }, extratime: { home: null, away: null }, penalty: { home: null, away: null } },
  },
];

// ─── Goal Animation Overlay ───────────────────────────────────────────────────
function GoalAnimation({ goal, onDone }: { goal: GoalAnimationPayload; onDone: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDone, 4000);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
      <div className="animate-goal-burst flex flex-col items-center gap-3">
        <div className="text-8xl animate-bounce">⚽</div>
        <div className="text-center">
          <div className="text-4xl font-black text-white tracking-tight" style={{ textShadow: '0 0 40px hsl(25 95% 53%)' }}>
            GOAL!
          </div>
          <div className="text-xl font-bold text-orange-400 mt-1">{goal.scorer}</div>
          <div className="text-base text-slate-300">{goal.team} · {goal.minute}&apos;</div>
        </div>
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-32 h-32 rounded-full border-4 border-orange-500/40 animate-ping" />
        <div className="absolute w-64 h-64 rounded-full border-2 border-orange-400/20 animate-ping" style={{ animationDelay: '0.2s' }} />
        <div className="absolute w-96 h-96 rounded-full border border-orange-300/10 animate-ping" style={{ animationDelay: '0.4s' }} />
      </div>
    </div>
  );
}

// ─── Match Card ───────────────────────────────────────────────────────────────
function MatchCard({ match, isHighlighted }: { match: LiveFixture; isHighlighted?: boolean }) {
  const { fixture, league, teams, goals } = match;
  const inPlay = isInPlay(fixture.status);
  const isHT = fixture.status.short === 'HT';
  const isNS = fixture.status.short === 'NS';
  const label = getStatusLabel(fixture.status);

  return (
    <Link href={`/game-detail?id=${fixture.id}`}>
      <div
        className="rounded-2xl p-4 cursor-pointer transition-all duration-200 hover:scale-[1.02] hover:shadow-2xl group"
        style={{
          background: isHighlighted
            ? 'linear-gradient(135deg, hsl(222 35% 13%) 0%, hsl(222 28% 16%) 100%)'
            : 'hsl(222 35% 11%)',
          border: `1px solid ${isHighlighted ? 'hsl(25 95% 53% / 0.3)' : 'hsl(222 25% 16%)'}`,
          boxShadow: isHighlighted ? '0 0 30px hsl(25 95% 53% / 0.08)' : 'none',
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-medium text-slate-500 truncate max-w-[55%]">{league.round}</span>
          <div className="flex items-center gap-1.5">
            {inPlay && (
              <span className="flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'hsl(0 84% 60% / 0.12)', color: 'hsl(0 84% 70%)' }}>
                <span className="w-1.5 h-1.5 rounded-full bg-red-400 live-pulse" />
                {label}
              </span>
            )}
            {isHT && (
              <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'hsl(38 92% 50% / 0.15)', color: 'hsl(38 92% 65%)' }}>HT</span>
            )}
            {isNS && (
              <span className="text-xs font-medium text-slate-500 px-2 py-0.5 rounded-full" style={{ background: 'hsl(222 25% 16%)' }}>
                {new Date(fixture.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 my-4">
          <div className="flex flex-col items-center gap-2 flex-1">
            {teams.home.logo ? (
              <img src={teams.home.logo} alt={teams.home.name} className="w-10 h-10 object-contain" />
            ) : (
              <span className="text-3xl">{getFlag(teams.home.name)}</span>
            )}
            <span className="text-xs font-semibold text-slate-300 text-center truncate w-full">{teams.home.name}</span>
          </div>

          <div className="flex flex-col items-center gap-1">
            {isNS ? (
              <span className="text-lg font-bold text-slate-500">vs</span>
            ) : (
              <div className="flex items-center gap-2">
                <span className="font-mono text-3xl font-black text-white tabular-nums">{goals.home ?? 0}</span>
                <span className="text-slate-600 text-xl">–</span>
                <span className="font-mono text-3xl font-black text-white tabular-nums">{goals.away ?? 0}</span>
              </div>
            )}
          </div>

          <div className="flex flex-col items-center gap-2 flex-1">
            {teams.away.logo ? (
              <img src={teams.away.logo} alt={teams.away.name} className="w-10 h-10 object-contain" />
            ) : (
              <span className="text-3xl">{getFlag(teams.away.name)}</span>
            )}
            <span className="text-xs font-semibold text-slate-300 text-center truncate w-full">{teams.away.name}</span>
          </div>
        </div>

        <div className="flex items-center justify-between pt-2" style={{ borderTop: '1px solid hsl(222 25% 14%)' }}>
          <span className="text-xs text-slate-600 truncate max-w-[70%]">{fixture.venue.name}</span>
          <span className="text-xs text-slate-600 group-hover:text-cyan-400 transition-colors">Details →</span>
        </div>
      </div>
    </Link>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────
function EmptyState({ filter }: { filter: string }) {
  const { t } = useLanguage();
  const messages: Record<string, string> = {
    live: t.liveScores.noLive,
    upcoming: t.common.noData,
    finished: t.common.noData,
    all: t.liveScores.noMatches,
  };
  return (
    <div className="rounded-2xl p-12 text-center" style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}>
      <div className="text-4xl mb-3">⚽</div>
      <p className="text-slate-300 font-semibold">{messages[filter] ?? t.liveScores.noMatches}</p>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function LiveScoresPage() {
  const [matches, setMatches] = useState<LiveFixture[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'live' | 'upcoming' | 'finished'>('all');
  const [lastRefresh, setLastRefresh] = useState<string>('');
  const [goalAnim, setGoalAnim] = useState<GoalAnimationPayload | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevRef = useRef<LiveFixture[]>([]);
  const { t } = useLanguage();
  const useLiveData = useShouldUseLiveData();

  const fetchMatches = useCallback(async () => {
    if (!useLiveData) {
      setMatches(SAMPLE_MATCHES);
      setLoading(false);
      setLastRefresh(new Date().toLocaleTimeString());
      return;
    }
    try {
      const [live, upcoming] = await Promise.all([
        getLiveWorldCupFixtures(),
        getUpcomingFixtures(20),
      ]);
      const combined = [...(live ?? []), ...(upcoming ?? [])];
      const seen = new Set<number>();
      const deduped = combined.filter(m => {
        if (seen.has(m.fixture.id)) return false;
        seen.add(m.fixture.id);
        return true;
      });

      deduped.forEach(match => {
        const prev = prevRef.current.find(m => m.fixture.id === match.fixture.id);
        if (prev) {
          const prevHome = prev.goals.home ?? 0;
          const prevAway = prev.goals.away ?? 0;
          const curHome = match.goals.home ?? 0;
          const curAway = match.goals.away ?? 0;
          if (prevHome !== curHome || prevAway !== curAway) {
            broadcastScoreUpdate({
              fixtureId: match.fixture.id,
              homeScore: curHome,
              awayScore: curAway,
              minute: match.fixture.status.elapsed ?? 0,
              status: match.fixture.status.short,
              homeName: match.teams.home.name,
              awayName: match.teams.away.name,
            });
          }
        }
      });

      prevRef.current = deduped;
      setMatches(deduped);
    } catch (err) {
      console.error('[LiveScores] fetch error:', err);
    } finally {
      setLoading(false);
      setLastRefresh(new Date().toLocaleTimeString());
    }
  }, [useLiveData]);

  useEffect(() => {
    fetchMatches();
    intervalRef.current = setInterval(fetchMatches, 60_000);

    const unsubScore = subscribeToScoreUpdates(payload => {
      setMatches(prev => prev.map(m =>
        m.fixture.id === payload.fixtureId
          ? { ...m, goals: { home: payload.homeScore, away: payload.awayScore }, fixture: { ...m.fixture, status: { ...m.fixture.status, elapsed: payload.minute, short: payload.status } } }
          : m
      ));
    });

    const unsubGoal = subscribeToGoalAnimations(payload => {
      setGoalAnim(payload);
    });

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (unsubScore) unsubScore();
      if (unsubGoal) unsubGoal();
    };
  }, [fetchMatches]);

  const filtered = matches.filter(m => {
    if (filter === 'live') return isInPlay(m.fixture.status) || m.fixture.status.short === 'HT';
    if (filter === 'upcoming') return m.fixture.status.short === 'NS';
    if (filter === 'finished') return ['FT', 'AET', 'PEN'].includes(m.fixture.status.short);
    return true;
  });

  const liveCount = matches.filter(m => isInPlay(m.fixture.status) || m.fixture.status.short === 'HT').length;

  return (
    <AppLayout activeRoute="/live-scores">
      {goalAnim && (
        <GoalAnimation goal={goalAnim} onDone={() => setGoalAnim(null)} />
      )}

      <div className="space-y-6 pb-20 lg:pb-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
              <Zap size={22} className="text-orange-400" />
              {t.liveScores.title}
            </h1>
            <p className="text-sm text-slate-400 mt-0.5">
              {useLiveData ? t.liveScores.subtitle : t.common.sampleDataNotice}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {lastRefresh && (
              <span className="flex items-center gap-1 text-xs text-slate-600">
                <RefreshCw size={10} className="text-cyan-500" />
                <span className="font-mono">{lastRefresh}</span>
              </span>
            )}
            <Link href="/live-chat" className="flex items-center gap-1.5 text-sm font-medium text-cyan-400 hover:text-cyan-300 transition-colors">
              <MessageCircle size={15} />
              <span className="hidden sm:inline">{t.chat.title}</span>
            </Link>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-thin pb-1">
          {([
            { key: 'all', label: t.liveScores.allMatches },
            { key: 'live', label: `${t.liveScores.live}${liveCount > 0 ? ` (${liveCount})` : ''}` },
            { key: 'upcoming', label: t.liveScores.upcoming },
            { key: 'finished', label: t.liveScores.finished },
          ] as const).map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-150 ${filter === tab.key ? 'bg-orange-500 text-white' : 'text-slate-400 hover:text-white'}`}
              style={filter !== tab.key ? { background: 'hsl(222 35% 13%)' } : {}}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Matches */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="rounded-2xl animate-pulse" style={{ background: 'hsl(222 35% 11%)', height: 200 }} />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState filter={filter} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((match, idx) => (
              <MatchCard key={match.fixture.id} match={match} isHighlighted={idx === 0 && (isInPlay(match.fixture.status) || match.fixture.status.short === 'HT')} />
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
